// Stato della sessione: utente collegato, ditte accessibili, ditta attiva.
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ErroreApi, esegui, type Schemi } from '../api/client';
import { impostaLingua, linguaAttiva, linguaIniziale, ricordaLinguaBrowser } from '../lingua';

const CHIAVE_DITTA = 'crm.dittaAttiva'; // preferenza del singolo browser: si può perdere senza danni

function leggiDittaSalvata(): string | null {
  try {
    return localStorage.getItem(CHIAVE_DITTA);
  } catch {
    return null;
  }
}

export const useSessione = defineStore('sessione', () => {
  const sessione = ref<Schemi['Sessione'] | null>(null);
  const caricata = ref(false);
  const dittaAttiva = ref<string | null>(leggiDittaSalvata());

  const utente = computed(() => sessione.value?.utente ?? null);
  const ambiente = computed(() => sessione.value?.ambiente ?? null);
  const ditte = computed(() => sessione.value?.ditte ?? []);
  const ditta = computed(() => ditte.value.find((d) => d.codice === dittaAttiva.value) ?? null);
  // La coda delle segnalazioni alla sede la gestiscono UFFICIO e DIREZIONE (D41); il backend lo controlla comunque
  const gestisceSegnalazioni = computed(() => ['UFFICIO', 'DIREZIONE'].includes(ditta.value?.ruolo?.trim().toUpperCase() ?? ''));
  // I cataloghi da inviare ai clienti li gestiscono gli stessi ruoli (D49); il backend lo controlla comunque
  const gestisceCataloghi = gestisceSegnalazioni;

  function imposta(s: Schemi['Sessione'] | null): void {
    sessione.value = s;
    caricata.value = true;
    // Lingua del profilo (D94); senza sessione resta quella del browser
    if (s && s.lingua !== linguaAttiva.value) void impostaLingua(s.lingua);
    // Ditta attiva: quella salvata se ancora accessibile, altrimenti l'unica disponibile
    const codici = s?.ditte.map((d) => d.codice) ?? [];
    if (!dittaAttiva.value || !codici.includes(dittaAttiva.value)) {
      scegliDitta(codici.length === 1 ? codici[0]! : null);
    }
  }

  function scegliDitta(codice: string | null): void {
    dittaAttiva.value = codice;
    try {
      if (codice) localStorage.setItem(CHIAVE_DITTA, codice);
      else localStorage.removeItem(CHIAVE_DITTA);
    } catch {
      /* archiviazione del browser non disponibile: pazienza */
    }
  }

  /** Legge la sessione dal server; nessuna sessione = null (non è un errore). */
  async function carica(): Promise<void> {
    try {
      imposta(await esegui(api.GET('/auth/sessione'), { ignora401: true }));
    } catch (e) {
      if (e instanceof ErroreApi && e.stato === 401) imposta(null);
      else throw e;
    }
  }

  async function login(login: string, password: string) {
    const r = await esegui(api.POST('/auth/login', { body: { login, password } }), { ignora401: true });
    if (r.stato === 'OK' && r.sessione) imposta(r.sessione);
    return r.stato;
  }

  async function verificaMfa(codice: string, ricordaDispositivo: boolean): Promise<void> {
    imposta(await esegui(api.POST('/auth/mfa', { body: { codice, ricordaDispositivo } }), { ignora401: true }));
  }

  function avviaConfigurazioneMfa() {
    return esegui(api.POST('/auth/mfa/configurazione'), { ignora401: true });
  }

  async function confermaConfigurazioneMfa(codice: string): Promise<void> {
    imposta(await esegui(api.POST('/auth/mfa/configurazione/conferma', { body: { codice } }), { ignora401: true }));
  }

  /** Preferenze dell'utente collegato (D42): ricevere o no il riepilogo del mattino. */
  async function impostaRiepilogo(riepilogo: boolean): Promise<void> {
    imposta(await esegui(api.PATCH('/auth/preferenze', { body: { riepilogo } })));
  }

  /** Cambio della password dell'utente collegato; poi si rilegge la sessione (il cambio obbligatorio, D92, è fatto). */
  async function cambiaPassword(attuale: string, nuova: string): Promise<void> {
    await esegui(api.POST('/auth/password/cambio', { body: { attuale, nuova } }));
    await carica();
  }

  /** Lingua dell'interfaccia (D94): con la sessione va nel profilo (anche per le email), senza resta su questo browser. */
  async function cambiaLingua(codice: string): Promise<void> {
    if (sessione.value) imposta(await esegui(api.PATCH('/auth/preferenze', { body: { lingua: codice } })));
    else ricordaLinguaBrowser(codice);
    await impostaLingua(codice);
  }

  async function logout(): Promise<void> {
    try {
      await esegui(api.POST('/auth/logout'), { ignora401: true });
    } finally {
      imposta(null);
      void impostaLingua(linguaIniziale());
    }
  }

  function dimentica(): void {
    imposta(null);
  }

  return {
    sessione, caricata, dittaAttiva, utente, ambiente, ditte, ditta, gestisceSegnalazioni, gestisceCataloghi,
    carica, login, verificaMfa, avviaConfigurazioneMfa, confermaConfigurazioneMfa, impostaRiepilogo, cambiaPassword, cambiaLingua, logout, scegliDitta, dimentica,
  };
});
